<app-page-shell variant="default">
<div class="widget-setting">

    <app-sb-input-form
      title="Widget settings"
      [stickyFooter]="true">
        @if (globalWidget) {
        <div class="row">
          <div class="col-md-12 mb-3">
            <div class="alert alert-info mb-0">
              Global Widget
              <a
                class="alert-link"
                [routerLink]="['/global-widgets', globalWidget.id, 'edit']"
                sbEntityLink>
                <strong>{{globalWidget.name}}</strong>
              </a>
              (<code>{{globalWidget.code}}</code>) is automatically applied to this project.
            </div>
          </div>
        </div>
        }
        <div class="text-info mb-3">To encourage new registrants to quickly engage with the campaign and start promoting it right after they register, we offer a special widget to be displayed on the registration confirmation page (thank you page). The widget automatically registers the new user on Snöball and generates his/her landing page.
          <br />
          <a href="https://i.snoball.local/share/c/V5p" target="_blank" rel="noopener">View an example</a>
        </div>
        <ng-template #useGlobalSetting let-key="key">
          @if (globalWidget) {
          <div class="widget-setting-meta widget-use-global-control ms-0">
            <button type="button"
                    class="widget-use-global-button"
                    [class.active]="isGlobalSettingInherited(key)"
                    [attr.aria-pressed]="isGlobalSettingInherited(key)"
                    (click)="toggleGlobalSetting(key, !isGlobalSettingInherited(key))">
              <i class="fa fa-link" aria-hidden="true"></i>
              <span>Use Global</span>
            </button>
            @if (isGlobalWidgetRichTextValue(key)) {
              @if (hasGlobalWidgetRichTextValue(key)) {
                <span class="global-value-popup-control">
                  <span>Global value:</span>
                  <button type="button"
                          class="global-value-popup-button"
                          aria-label="View global value"
                          title="View global value"
                          placement="top"
                          container="body"
                          [autoClose]="'outside'"
                          [popoverClass]="'global-widget-rich-text-popover'"
                          [ngbPopover]="globalRichTextValue">
                    <i class="fa fa-eye" aria-hidden="true"></i>
                  </button>
                </span>
                <ng-template #globalRichTextValue>
                  <div class="global-widget-rich-text-content"
                       [innerHTML]="getGlobalWidgetFormattedValue(key)">
                  </div>
                </ng-template>
              } @else {
                <span>Global value: Not set</span>
              }
            } @else {
              <span>Global value: {{getGlobalWidgetValueLabel(key)}}</span>
            }
          </div>
          }
        </ng-template>

        <!-- Every setting below is off while the widget is inactive, so the switch sits above the tabs. -->
        <div class="widget-activation">
          <app-sb-input-checkbox
            mode="switch"
            [id]="'customizePage'"
            label="Widget Active"
            [disabled]="isGlobalSettingInherited('active')"
            [(ngModel)]="projectMeta['active']">
          </app-sb-input-checkbox>
          <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'active'}"></ng-container>
        </div>

        <app-sb-tabs class="widget-setting-tabs" queryParam="tab">
          <ng-template sbTab="activate" title="Activate">
            <div class="widget-tab-pane">
              <div class="widget-field">
                <app-sb-input-select
                  [id]="'default_new_register_campaign_id'"
                  label="Linked Campaign"
                  description="New registrations will be added to the following campaign (Mandatory)"
                  placeholder="Select Default Campaign"
                  [items]="campaigns"
                  [disabled]="!projectMeta['active']"
                  [ngModel]="projectMeta['default_new_register_campaign_id']"
                  (ngModelChange)="projectMeta['default_new_register_campaign_id'] = $event ?? null">
                </app-sb-input-select>
              </div>
              <div class="widget-field">
                <app-sb-input-select
                  [id]="'conditional_new_register_campaign_id'"
                  label="Linked Conditional Campaign"
                  description="This can be used for new registrations in special cases defined in custom script section (Ignore if you don't know what this is)"
                  placeholder="Select Conditional Campaign"
                  [items]="campaigns"
                  [disabled]="!projectMeta['active']"
                  [ngModel]="projectMeta['conditional_new_register_campaign_id']"
                  (ngModelChange)="projectMeta['conditional_new_register_campaign_id'] = $event ?? null">
                </app-sb-input-select>
              </div>
              @if (!globalWidget) {
              <div class="widget-field">
                <div class="widget-inline-row">
                  <app-sb-input-select
                    class="widget-inline-row__grow"
                    [id]="'registration_platform_id'"
                    label="Registration Platform"
                    description="We already support several different registration platforms, if you find yours and select it here it makes the integration much easier"
                    placeholder="Select Registration Platforms"
                    labelKey="platform_name_with_support"
                    [items]="registrationPlatforms"
                    [disabled]="!projectMeta['active'] || isGlobalSettingInherited('registration_platform_id')"
                    [ngModel]="projectMeta['registration_platform_id']"
                    (ngModelChange)="onSelectRegistrationPlatform($event != null ? {value: $event} : null)">
                  </app-sb-input-select>
                  @if (currentUser?.can('index.all', 'project')) {
                  <app-sb-button variant="default" icon="fa fa-plus" label="Add New Platform" type="button"
                    (clicked)="showAddPlatformPopup()"></app-sb-button>
                  }
                </div>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'registration_platform_id'}"></ng-container>
              </div>
              }
              <div class="widget-field">
                <app-sb-input-textarea
                  [id]="'note'"
                  label="Note"
                  description="Can include info about special consideration regarding the widget or its integration like for example PromoCodes for testing, where it might have been added in your site, etc"
                  [rows]="6"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('note')"
                  [(ngModel)]="projectMeta['note']">
                </app-sb-input-textarea>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'note'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-copy-field
                  class="widget-project-code"
                  [id]="'project_code'"
                  label="Project Code"
                  description="This is mostly useful when the widget is already integrated in your site in a generic way and all you need is to just add the project code somewhere in your platform's setting"
                  successMessage="Project Code is Copied"
                  [value]="project?.code">
                </app-sb-copy-field>
              </div>
              <div class="widget-field">
                <div class="widget-field__hint">Now that you've set the settings, have a look at the detailed generated instruction on how to add the widget.</div>
                <app-sb-button variant="link" icon="fa fa-question-circle" label="Widget integration instructions" type="button"
                  extraClass="px-0" (clicked)="showWidgetInstructions(widgetInstructions)"></app-sb-button>
              </div>
            </div>
          </ng-template>

          <ng-template sbTab="influencer" title="Influencers Personalization Options">
            <div class="widget-tab-pane">
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'allow_influencers_to_edit_primary_info'"
                  label="Allow influencer to edit his/her primary info through the widget"
                  [disabled]="!projectMeta['active']"
                  [(ngModel)]="projectMeta['allow_influencers_to_edit_primary_info']">
                </app-sb-input-checkbox>
              </div>
              <div class="widget-field">
                <div class="widget-check-row">
                  <app-sb-input-checkbox
                    [id]="'allow_influencers_to_update_their_picture_in_widget'"
                    label="Allow influencers to update their picture"
                    [disabled]="!projectMeta['active']"
                    [(ngModel)]="projectMeta['allow_influencers_to_update_their_picture_in_widget']">
                  </app-sb-input-checkbox>
                  <app-sb-button variant="link" size="sm" label="Advanced Settings" type="button" extraClass="px-0"
                    [disabled]="!projectMeta['active']"
                    (clicked)="openWidgetAdvancedSettings(advancedImageSettingsModal)"></app-sb-button>
                </div>
                <div class="widget-field__hint widget-field__hint--check">A picture button will be displayed on the share widget for the campaign that's linked to the widget. Influencer will be able to update their picture through a popup and see the result on the share image.</div>
              </div>
              <app-sb-premium-lock feature="Influencer video upload" [locked]="!currentUser?.can('unrestricted-use-video-upload', 'project')">
                <div class="widget-field">
                  <div class="widget-check-row">
                    <app-sb-input-checkbox
                      [id]="'allow_influencers_to_update_their_video_in_widget'"
                      label="Allow influencers to update their video"
                      [disabled]="!projectMeta['active']"
                      [(ngModel)]="projectMeta['allow_influencers_to_update_their_video_in_widget']">
                    </app-sb-input-checkbox>
                    <app-sb-premium-badge feature="Influencer video upload"></app-sb-premium-badge>
                    <app-sb-button variant="link" size="sm" label="Advanced Settings" type="button" extraClass="px-0"
                      [disabled]="!projectMeta['active']"
                      (clicked)="openWidgetAdvancedSettings(advancedVideoSettingsModal)"></app-sb-button>
                  </div>
                  <div class="widget-field__hint widget-field__hint--check">A video button will be displayed on the share widget for the campaign that's linked to the widget. Influencers will be able to update their video through the widget.</div>
                </div>
              </app-sb-premium-lock>
              <app-sb-premium-lock feature="AI Avatar" [locked]="!currentUser?.can('unrestricted-use-ai-avatar', 'project')">
                <div class="widget-field">
                  <div class="widget-check-row">
                    <app-sb-input-checkbox
                      [id]="'allow_influencers_to_use_ai_avatar_in_widget'"
                      label="Allow influencers to use AI to generate their Picture/Avatar"
                      [disabled]="!projectMeta['allow_influencers_to_update_their_picture_in_widget'] || !projectMeta['active']"
                      [(ngModel)]="projectMeta['allow_influencers_to_use_ai_avatar_in_widget']">
                    </app-sb-input-checkbox>
                    <app-sb-premium-badge feature="AI Avatar"></app-sb-premium-badge>
                  </div>
                  <div class="widget-field__hint widget-field__hint--check">When uploading their picture through widget upload picture, users will also have the option to use Avatar AI to style their picture and use that as their picture instead</div>
                </div>
                <div class="widget-field widget-avatar-template">
                  <app-sb-input-select
                    [id]="'widget_ai_avatar_template'"
                    class="widget-avatar-template__select"
                    label="AI Avatar Template"
                    placeholder="Select Widget AI Avatar Template"
                    imageKey="selectedImageVariation.url"
                    [items]="widgetAIAvatarTemplates"
                    [clearable]="false"
                    [disabled]="!projectMeta['allow_influencers_to_use_ai_avatar_in_widget'] || widgetAIAvatarTemplates.length === 0 || !projectMeta['active']"
                    [ngModel]="projectMeta['widget_ai_avatar_template'] || null"
                    (ngModelChange)="selectWidgetAIAvatarTemplate($event)">
                  </app-sb-input-select>
                  <app-sb-button variant="default" icon="fa fa-picture-o" label="Customize" type="button"
                    [disabled]="!projectMeta['active']"
                    (clicked)="showCustomizeAvatarTemplatePopup()"></app-sb-button>
                </div>
              </app-sb-premium-lock>
            </div>
          </ng-template>

          <ng-template sbTab="test" title="Test">
            <div class="widget-tab-pane">
              <div class="text-info">While you're adding the widget to your site, you may want to hide it from your users.</div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'limit_access'"
                  label="Limit access for testing purpose"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('whitelist_access')"
                  [(ngModel)]="projectMeta['whitelist_access']"
                  (change)="changeCheckBox($event)">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'whitelist_access'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'autoAddId'"
                  label="Enable Adding IP By Opening This URL"
                  description="Opening the following URL adds your IP to the whitelist, this way the widget will only show up on your site when you're visiting"
                  [disabled]="!projectMeta['active'] || !projectMeta['whitelist_access'] || isGlobalSettingInherited('auto_add_ip')"
                  [(ngModel)]="projectMeta['auto_add_ip']">
                </app-sb-input-checkbox>
                <app-sb-copy-field class="widget-field__below-check" [id]="'ip_limit_url'" [value]="ipLimitUrl"></app-sb-copy-field>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'auto_add_ip'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-textarea
                  [id]="'limit_access_ips'"
                  label="Whitelisted IPs"
                  description="Only people coming from the following IPs can see the widget. Clear it to let everyone see the widget (Separate by enter)"
                  placeholder="34.54.54.66&#10;44.33.123.45"
                  [rows]="5"
                  [disabled]="!projectMeta['active'] || !projectMeta['whitelist_access'] || isGlobalSettingInherited('limit_access_ips')"
                  [(ngModel)]="projectMeta['limit_access_ips']">
                </app-sb-input-textarea>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'limit_access_ips'}"></ng-container>
              </div>
            </div>
          </ng-template>

          <ng-template sbTab="visual" title="Visual">
            <div class="widget-tab-pane">
              <div class="text-info">Customize widget's look and feel. If you want to see the result you can just click on the Save button at the buttom and then click on Preview</div>
              <div class="widget-field" style="display: none">
                <app-sb-input-checkbox
                  [id]="'enable_linkedin_integration_before_registration'"
                  label="Enable Linkedin Integration before registration"
                  [disabled]="!projectMeta['active']"
                  [(ngModel)]="projectMeta['enable_linkedin_integration_before_registration']">
                </app-sb-input-checkbox>
              </div>
              <div class="widget-field">
                <app-sb-input-language
                  label="Language"
                  id="language"
                  [clearable]="false"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('language')"
                  [(ngModel)]="projectMeta['language']">
                </app-sb-input-language>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'language'}"></ng-container>
              </div>
              <div class="widget-field">
                <div class="widget-text-editor-label-row">
                  <small class="text-capitalize">Header 1</small>
                  <div class="inputs">
                    <app-variables
                      [isDisabled]="!projectMeta['active'] || isGlobalSettingInherited('header_1')"
                      [variableClickFor]="'header_1'"
                      (onVariableClick)="onWidgetTextEditorVariableClick($event, 'header_1', projectHeader1Editor)"
                    ></app-variables>
                    <app-file-browser
                      [clientId]="$safeNavigationMigration(project?.client_id)"
                      [projectId]="$safeNavigationMigration(project?.id)"
                      [enableChoose]="true"
                      [disabled]="!projectMeta['active'] || isGlobalSettingInherited('header_1')"
                      (fileSelected)="onWidgetTextEditorFileSelected($event, 'header_1')">
                    </app-file-browser>
                  </div>
                </div>
                <app-sb-rich-text
                  #projectHeader1Editor
                  class="share-widget-text-editor share-widget-text-editor-compact"
                  [tools]="getWidgetTextEditorConfig('header_1').tools"
                  [height]="getWidgetTextEditorConfig('header_1').height"
                  [multiline]="getWidgetTextEditorConfig('header_1').multiline"
                  [html]="projectMeta['header_1'] || ''"
                  [placeholder]="getWidgetHeaderPlaceholder('header_1')"
                  [readOnly]="!projectMeta['active'] || isGlobalSettingInherited('header_1')"
                  (htmlChange)="onWidgetTextEditorChange('header_1', $event)"
                  shape="inline"
                  [inlineStyles]="true"
                  [linkUnderlineToggle]="true"
                  [autoGrow]="true"
                  [resizable]="true">
                </app-sb-rich-text>
                <div class="widget-field__hint">{{ widgetHeaderDescriptions.header_1 }}</div>
                @if (getWidgetHeaderNotLiveNote('header_1'); as note) {
                <div class="widget-field__hint">
                  {{ note.label }} @if (note.text) {<span [innerHTML]="note.text"></span>}
                </div>
                }
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'header_1'}"></ng-container>
              </div>
              <div class="widget-field">
                <div class="widget-text-editor-label-row">
                  <small class="text-capitalize">Header 2</small>
                  <div class="inputs">
                    <app-variables
                      [isDisabled]="!projectMeta['active'] || isGlobalSettingInherited('header_2')"
                      [variableClickFor]="'header_2'"
                      (onVariableClick)="onWidgetTextEditorVariableClick($event, 'header_2', projectHeader2Editor)"
                    ></app-variables>
                    <app-file-browser
                      [clientId]="$safeNavigationMigration(project?.client_id)"
                      [projectId]="$safeNavigationMigration(project?.id)"
                      [enableChoose]="true"
                      [disabled]="!projectMeta['active'] || isGlobalSettingInherited('header_2')"
                      (fileSelected)="onWidgetTextEditorFileSelected($event, 'header_2')">
                    </app-file-browser>
                  </div>
                </div>
                <app-sb-rich-text
                  #projectHeader2Editor
                  class="share-widget-text-editor share-widget-text-editor-compact"
                  [tools]="getWidgetTextEditorConfig('header_2').tools"
                  [height]="getWidgetTextEditorConfig('header_2').height"
                  [multiline]="getWidgetTextEditorConfig('header_2').multiline"
                  [html]="projectMeta['header_2'] || ''"
                  [placeholder]="getWidgetHeaderPlaceholder('header_2')"
                  [readOnly]="!projectMeta['active'] || isGlobalSettingInherited('header_2')"
                  (htmlChange)="onWidgetTextEditorChange('header_2', $event)"
                  shape="inline"
                  [inlineStyles]="true"
                  [linkUnderlineToggle]="true"
                  [autoGrow]="true"
                  [resizable]="true">
                </app-sb-rich-text>
                <div class="widget-field__hint">{{ widgetHeaderDescriptions.header_2 }}</div>
                @if (getWidgetHeaderNotLiveNote('header_2'); as note) {
                <div class="widget-field__hint">
                  {{ note.label }} @if (note.text) {<span [innerHTML]="note.text"></span>}
                </div>
                }
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'header_2'}"></ng-container>
              </div>
              <div class="widget-field">
                <div class="widget-text-editor-label-row">
                  <small class="text-capitalize">Header 3</small>
                  <div class="inputs">
                    <app-variables
                      [isDisabled]="!projectMeta['active'] || isGlobalSettingInherited('header_3')"
                      [variableClickFor]="'header_3'"
                      (onVariableClick)="onWidgetTextEditorVariableClick($event, 'header_3', projectHeader3Editor)"
                    ></app-variables>
                    <app-file-browser
                      [clientId]="$safeNavigationMigration(project?.client_id)"
                      [projectId]="$safeNavigationMigration(project?.id)"
                      [enableChoose]="true"
                      [disabled]="!projectMeta['active'] || isGlobalSettingInherited('header_3')"
                      (fileSelected)="onWidgetTextEditorFileSelected($event, 'header_3')">
                    </app-file-browser>
                  </div>
                </div>
                <app-sb-rich-text
                  #projectHeader3Editor
                  class="share-widget-text-editor share-widget-text-editor-compact"
                  [tools]="getWidgetTextEditorConfig('header_3').tools"
                  [height]="getWidgetTextEditorConfig('header_3').height"
                  [multiline]="getWidgetTextEditorConfig('header_3').multiline"
                  [html]="projectMeta['header_3'] || ''"
                  [placeholder]="getWidgetHeaderPlaceholder('header_3')"
                  [readOnly]="!projectMeta['active'] || isGlobalSettingInherited('header_3')"
                  (htmlChange)="onWidgetTextEditorChange('header_3', $event)"
                  shape="inline"
                  [inlineStyles]="true"
                  [linkUnderlineToggle]="true"
                  [autoGrow]="true"
                  [resizable]="true">
                </app-sb-rich-text>
                <div class="widget-field__hint">{{ widgetHeaderDescriptions.header_3 }}</div>
                @if (getWidgetHeaderNotLiveNote('header_3'); as note) {
                <div class="widget-field__hint">
                  {{ note.label }} @if (note.text) {<span [innerHTML]="note.text"></span>}
                </div>
                }
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'header_3'}"></ng-container>
              </div>
              <div class="widget-field">
                <div class="widget-text-editor-label-row">
                  <small class="text-capitalize">Promo Text</small>
                </div>
                <app-sb-rich-text
                  #projectPromoTextEditor
                  class="share-widget-text-editor"
                  [tools]="getWidgetTextEditorConfig('promo_text').tools"
                  [height]="getWidgetTextEditorConfig('promo_text').height"
                  [multiline]="getWidgetTextEditorConfig('promo_text').multiline"
                  [html]="projectMeta['promo_text'] || ''"
                  [readOnly]="!projectMeta['active'] || isGlobalSettingInherited('promo_text')"
                  (htmlChange)="onWidgetTextEditorChange('promo_text', $event)"
                  shape="inline"
                  [inlineStyles]="true"
                  [linkUnderlineToggle]="true"
                  [autoGrow]="true"
                  [resizable]="true">
                </app-sb-rich-text>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'promo_text'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-text
                  [id]="'open_message_text'"
                  label="Custom Minimized Text"
                  placeholder="Invite someone!"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('open_message_text')"
                  [(ngModel)]="projectMeta['open_message_text']">
                </app-sb-input-text>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'open_message_text'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'minimized_notification_message_disabled'"
                  label="Disable Minimized Notification Message"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('minimized_notification_message_disabled')"
                  [(ngModel)]="projectMeta['minimized_notification_message_disabled']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'minimized_notification_message_disabled'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'hide_when_closed'"
                  label="Hide when closed"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('hide_when_closed')"
                  [(ngModel)]="projectMeta['hide_when_closed']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'hide_when_closed'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-text
                  [id]="'minimized_notification_message'"
                  label="Custom Minimized Notification Message"
                  placeholder="Click/Touch to easily invite others"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('minimized_notification_message')"
                  [(ngModel)]="projectMeta['minimized_notification_message']">
                </app-sb-input-text>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'minimized_notification_message'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'user_agreement_checkbox_disabled'"
                  label="Disable User Agreement Checkbox"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('user_agreement_checkbox_disabled')"
                  [(ngModel)]="projectMeta['user_agreement_checkbox_disabled']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'user_agreement_checkbox_disabled'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'show_preload'"
                  label="Show Preload"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('show_preload')"
                  [(ngModel)]="projectMeta['show_preload']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'show_preload'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-select
                  [id]="'display_mode'"
                  label="Display Mode"
                  [items]="displayModeOptions"
                  [clearable]="false"
                  [autocomplete]="false"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('display_mode')"
                  [(ngModel)]="projectMeta['display_mode']">
                </app-sb-input-select>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'display_mode'}"></ng-container>
              </div>
              <div class="widget-field">
                <!-- The empty value is the widget's default state; the list names it "Default". -->
                <app-sb-input-select
                  [id]="'display_mode_state'"
                  label="Display State"
                  [items]="displayModeStateOptions"
                  [clearable]="false"
                  [autocomplete]="false"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('display_mode_state')"
                  [ngModel]="projectMeta['display_mode_state'] || defaultDisplayModeState"
                  (ngModelChange)="projectMeta['display_mode_state'] = $event === defaultDisplayModeState ? '' : $event">
                </app-sb-input-select>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'display_mode_state'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-select
                  [id]="'linkedin_share_method'"
                  label="Linkedin Share Method"
                  [items]="linkedinShareMethodOptions"
                  [clearable]="false"
                  [autocomplete]="false"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('linkedin_share_method')"
                  [ngModel]="projectMeta['linkedin_share_method'] || 'basic'"
                  (ngModelChange)="projectMeta['linkedin_share_method'] = $event">
                </app-sb-input-select>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'linkedin_share_method'}"></ng-container>
              </div>
              <div class="widget-color-grid">
                @for (color of themeColorFields; track color.key) {
                <div class="widget-field">
                  <app-sb-input-color
                    [id]="color.key"
                    [label]="color.label"
                    [description]="'Default ' + color.defaultValue"
                    [disabled]="!projectMeta['active'] || isGlobalSettingInherited(color.key)"
                    [(ngModel)]="projectMeta[color.key]">
                  </app-sb-input-color>
                  <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: color.key}"></ng-container>
                </div>
                }
              </div>
              <div class="widget-field">
                <app-sb-input-textarea
                  [id]="'fontface'"
                  label="Custom FontFace"
                  placeholder="@font-face {
  font-family: myFirstFont;
  src: url(https://example.com/sansation_light.woff);
}"
                  [rows]="8"
                  [(ngModel)]="projectMeta['fontface']">
                </app-sb-input-textarea>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'font_face'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-text
                  [id]="'font_family'"
                  label="Font Family"
                  placeholder="'myFirstFont', 'omnes', Arial, 'Helvetica Neue', Helvetica, sans-serif"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('font_family')"
                  [(ngModel)]="projectMeta['font_family']">
                </app-sb-input-text>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'font_family'}"></ng-container>
              </div>
              <div class="widget-field language-fonts">
                <div class="widget-field__label">Language-specific fonts</div>
                <div class="text-info mb-2">
                  Optional. Use a different font when the widget renders in a given language &mdash;
                  useful when your brand font has no glyphs for that script. Languages not listed here
                  use the Font Family above. If the font is not a system font, declare its
                  &#64;font-face once in Custom FontFace above.
                </div>
                @for (languageFont of languageFonts; track languageFont; let i = $index) {
                <div class="language-font-row mb-3">
                  <div class="language-font-row__fields">
                    <div class="flex-fill">
                      <app-sb-input-language
                        label="Language"
                        [id]="'language_font_' + i"
                        [disabled]="!projectMeta['active'] || isGlobalSettingInherited('font_by_language')"
                        [(ngModel)]="languageFont.language">
                      </app-sb-input-language>
                    </div>
                    <div class="flex-fill">
                      <app-sb-input-text
                        label="Font Family"
                        [id]="'language_font_family_' + i"
                        [disabled]="!projectMeta['active'] || isGlobalSettingInherited('font_by_language')"
                        placeholder="'Noto Sans JP', sans-serif"
                        [(ngModel)]="languageFont.font_family">
                      </app-sb-input-text>
                    </div>
                    <app-sb-button
                      class="language-font-remove"
                      variant="ghost"
                      size="sm"
                      shape="circle"
                      icon="fa fa-trash"
                      extraClass="text-danger"
                      ariaLabel="Remove language-specific font"
                      [disabled]="!projectMeta['active'] || isGlobalSettingInherited('font_by_language')"
                      (clicked)="removeLanguageFont(i)">
                    </app-sb-button>
                  </div>
                  @if (isDuplicateLanguageFont(i)) {
                  <div class="text-danger small mt-1">
                    This language is already configured above &mdash; only the first entry is used.
                  </div>
                  }
                </div>
                }
                <app-sb-button variant="default" size="sm" icon="fa fa-plus" label="Add language" type="button"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('font_by_language')"
                  (clicked)="addLanguageFont()"></app-sb-button>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'font_by_language'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-number
                  [id]="'font_size'"
                  label="Font Size (px)"
                  placeholder="1"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('font_size')"
                  [(ngModel)]="projectMeta['font_size']">
                </app-sb-input-number>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'font_size'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'show_widget_ui_customization_on_all_widgets'"
                  label="Show widget UI customization on all widgets"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('show_widget_ui_customization_on_all_widgets')"
                  [(ngModel)]="projectMeta['show_widget_ui_customization_on_all_widgets']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'show_widget_ui_customization_on_all_widgets'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'enable_joined_influencer_cookie'"
                  label="Show the minimized widget on the site user previously joined via widget"
                  [disabled]="!projectMeta['active'] || projectMeta['display_mode'] === 'hidden' || isGlobalSettingInherited('enable_joined_influencer_cookie')"
                  [(ngModel)]="projectMeta['enable_joined_influencer_cookie']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'enable_joined_influencer_cookie'}"></ng-container>
              </div>
            </div>
          </ng-template>

          @if (currentUser?.can('index.all', 'project') && !globalWidget) {
          <ng-template sbTab="advanced" title="Advanced">
            <div class="widget-tab-pane">
              <div class="text-info">This is for more advanced customization and integration. Such as when the widget doesn't work or your site needs special customization for the widget to work</div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'referral_append_query_string_to_all_links'"
                  label="Add the referral tracking parameter to other links on the page"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('referral_append_query_string_to_all_links')"
                  [(ngModel)]="projectMeta['referral_append_query_string_to_all_links']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'referral_append_query_string_to_all_links'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-textarea
                  [id]="'referral_allowed_domains'"
                  label="Auto add referral allowed domains"
                  description="Separate by enter. Leave blank to disable this feature"
                  placeholder="example.com&#10;test.example.com&#10;*.example.com&#10;exampe*.com"
                  [rows]="5"
                  [disabled]="!projectMeta['active'] || !projectMeta['referral_append_query_string_to_all_links'] || isGlobalSettingInherited('referral_allowed_domains')"
                  [(ngModel)]="projectMeta['referral_allowed_domains']">
                </app-sb-input-textarea>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'referral_allowed_domains'}"></ng-container>
              </div>
              @for (toggle of widgetBehaviorToggles; track toggle) {
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="toggle.key"
                  [label]="toggle.label"
                  [disabled]="!projectMeta['active']"
                  [(ngModel)]="projectMeta[toggle.key]">
                </app-sb-input-checkbox>
              </div>
              }
              @if (currentUser?.can('index.all', 'project')) {
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'google_analytics_disabled'"
                  label="Disable Google Analytics Tracking For Embedded Widget Visits"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('google_analytics_disabled')"
                  [(ngModel)]="projectMeta['google_analytics_disabled']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'google_analytics_disabled'}"></ng-container>
              </div>
              }
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'remoteLog'"
                  label="Remote Log"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('remote_log_disabled')"
                  [(ngModel)]="remoteLogEnabled">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'remote_log_disabled'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'debug'"
                  label="Debug"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('debug')"
                  [(ngModel)]="projectMeta['debug']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'debug'}"></ng-container>
              </div>
              @if (currentUser?.can('index.all', 'project') && widgetCustomizationCode && this.projectMeta['registration_platform_id']) {
              <div class="widget-field">
                <app-sb-copy-field
                  [id]="'widget_code'"
                  label="Platform's Default Script"
                  [multiline]="true"
                  [rows]="10"
                  [value]="widgetCustomizationCode">
                </app-sb-copy-field>
              </div>
              }
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'registration_platform_default_custom_js'"
                  label="Use registration platform's default script"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('registration_platform_default_custom_js')"
                  [(ngModel)]="projectMeta['registration_platform_default_custom_js']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'registration_platform_default_custom_js'}"></ng-container>
              </div>
              <div class="widget-field">
                <div class="widget-field__label-row">
                  <span class="widget-field__label">Custom Script</span>
                  <app-sb-button variant="link" size="sm" icon="fa fa-question-circle" label="View Guide" type="button"
                    extraClass="px-0" (clicked)="showCustomScriptInstructions(widgetCustomScriptInstructions)"></app-sb-button>
                </div>
                <div class="widget-code-editor">
                  <app-ace-editor #editor style="min-height: 200px;"
                              [autoUpdateContent]="true"
                              [mode]="'javascript'"
                              [options]="aceEditorOptions"
                              [(text)]="projectMeta[customScriptKey]"
                              [readOnly]="!projectMeta['active'] || isGlobalSettingInherited('custom_script')">
                  </app-ace-editor>
                </div>
                @if (canGenerateWidgetCustomization) {
                <app-sb-button class="widget-field__action" variant="primary" size="sm" icon="fa fa-magic" label="Generate with AI" type="button"
                  (clicked)="openWidgetCustomizationAi()"></app-sb-button>
                }
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'custom_script'}"></ng-container>
              </div>
              @if (currentUser?.can('index.all', 'project') && widgetCustomizationCss && this.projectMeta['registration_platform_id']) {
              <div class="widget-field">
                <app-sb-copy-field
                  [id]="'widget_css'"
                  label="Platform's Default Css"
                  [multiline]="true"
                  [rows]="10"
                  [value]="widgetCustomizationCss">
                </app-sb-copy-field>
              </div>
              }
              <div class="widget-field">
                <app-sb-input-checkbox
                  [id]="'registration_platform_default_custom_css'"
                  label="Use registration platform's default CSS"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('registration_platform_default_custom_css')"
                  [(ngModel)]="projectMeta['registration_platform_default_custom_css']">
                </app-sb-input-checkbox>
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'registration_platform_default_custom_css'}"></ng-container>
              </div>
              <div class="widget-field">
                <app-sb-input-textarea
                  [id]="'custom_css'"
                  label="Custom CSS"
                  [rows]="10"
                  [disabled]="!projectMeta['active'] || isGlobalSettingInherited('custom_css')"
                  [(ngModel)]="projectMeta[customCssKey]">
                </app-sb-input-textarea>
                @if (canGenerateWidgetCustomization) {
                <app-sb-button class="widget-field__action" variant="primary" size="sm" icon="fa fa-magic" label="Generate with AI" type="button"
                  (clicked)="openWidgetCustomizationAi()"></app-sb-button>
                }
                <ng-container *ngTemplateOutlet="useGlobalSetting; context: {key: 'custom_css'}"></ng-container>
              </div>
            </div>
          </ng-template>
          }
        </app-sb-tabs>
        <ng-template sbInputFormActions>
          <div class="sb-form__footer-actions">
            <app-sb-button variant="default" label="Preview" icon="fa fa-eye" type="button"
              [disabled]="!projectMeta['active']" (clicked)="previewWidget()"></app-sb-button>
            @if (currentUser?.can('index.all', 'project')) {
            <app-sb-button
              variant="default" label="Start Widget Integration Assistant Session" icon="fa fa-comments" type="button"
              [disabled]="!projectMeta['active']" (clicked)="startWidgetIntegrationAssistantSession()"></app-sb-button>
            }
            <app-sb-button variant="primary" label="Save" icon="fa fa-check" type="button"
              (clicked)="shareWidgetPreview()"></app-sb-button>
          </div>
        </ng-template>
      </app-sb-input-form>

</div>
</app-page-shell>

<ng-template #advancedImageSettingsModal let-modal>
  <div class="modal-header">
    <h4 class="modal-title">Advanced Image Settings</h4>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('cancel')"></button>
  </div>
  <div class="modal-body">
    <h6 class="text-uppercase text-muted fw-bold mb-3">Size</h6>
    <div class="row">
      <div class="col-md-6">
        <div class="mb-3">
          <label>Min Width</label>
          <div class="input-group">
          <input type="number"
                 min="0"
                 class="form-control"
                 [(ngModel)]="projectMeta['widget_profile_picture_min_width']"
                 placeholder="0">
            <span class="input-group-text">px</span>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="mb-3">
          <label>Min Height</label>
          <div class="input-group">
          <input type="number"
                 min="0"
                 class="form-control"
                 [(ngModel)]="projectMeta['widget_profile_picture_min_height']"
                 placeholder="10">
            <span class="input-group-text">px</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <app-sb-button variant="default" label="Cancel" icon="fa fa-times" type="button" (clicked)="modal.dismiss('cancel')"></app-sb-button>
    <app-sb-button variant="primary" label="Save Settings" icon="fa fa-check" type="button" (clicked)="saveWidgetAdvancedSettings(modal)"></app-sb-button>
  </div>
</ng-template>

<ng-template #advancedVideoSettingsModal let-modal>
  <div class="modal-header">
    <h4 class="modal-title">Advanced Video Settings</h4>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('cancel')"></button>
  </div>
  <div class="modal-body">
    <h6 class="text-uppercase text-muted fw-bold mb-3">Recording</h6>
    <div class="mb-3">
      <app-sb-input-checkbox
        [id]="'project_widget_profile_video_enable_recorder'"
        label="Enable Recorder"
        [(ngModel)]="projectMeta['widget_profile_video_enable_recorder']">
      </app-sb-input-checkbox>
    </div>
    <div class="row">
      <div class="col-md-6">
        <div class="mb-3">
          <label>Min Duration</label>
          <div class="input-group">
            <input type="number" min="0" class="form-control" [(ngModel)]="projectMeta['widget_profile_video_min_duration']" placeholder="0">
            <span class="input-group-text">sec</span>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="mb-3">
          <label>Max Duration</label>
          <div class="input-group">
            <input type="number" min="0" class="form-control" [(ngModel)]="projectMeta['widget_profile_video_max_duration']" placeholder="10">
            <span class="input-group-text">sec</span>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="mb-3">
          <label>Min Width</label>
          <div class="input-group">
            <input type="number" min="0" class="form-control" [(ngModel)]="projectMeta['widget_profile_video_min_width']" placeholder="0">
            <span class="input-group-text">px</span>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="mb-3">
          <label>Min Height</label>
          <div class="input-group">
            <input type="number" min="0" class="form-control" [(ngModel)]="projectMeta['widget_profile_video_min_height']" placeholder="0">
            <span class="input-group-text">px</span>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="mb-3">
          <label>Target Aspect Width</label>
          <div class="input-group">
            <input type="number" min="0" class="form-control" [(ngModel)]="projectMeta['widget_profile_video_target_aspect_width']" placeholder="0">
            <span class="input-group-text">ratio</span>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="mb-3">
          <label>Target Aspect Height</label>
          <div class="input-group">
            <input type="number" min="0" class="form-control" [(ngModel)]="projectMeta['widget_profile_video_target_aspect_height']" placeholder="0">
            <span class="input-group-text">ratio</span>
          </div>
        </div>
      </div>
    </div>

    @if (projectMeta['widget_profile_video_enable_recorder']) {
    <h6 class="text-uppercase text-muted fw-bold mb-3 mt-3">Captions</h6>
    <div class="mb-3">
      <app-sb-input-radio
        label="Caption Mode"
        name="project_caption_mode"
        layout="inline"
        [options]="videoModeOptions"
        [(ngModel)]="projectMeta['widget_profile_video_caption_mode']">
      </app-sb-input-radio>
    </div>
    @if (projectMeta['widget_profile_video_caption_mode'] !== 'disabled') {
    <div class="row">
      <div class="col-md-12">
        <div class="mb-3">
          <label>Caption Font</label>
          <div class="input-group">
            <input type="text"
                   class="form-control"
                   [value]="getVideoCaptionFontLabel(projectMeta)"
                   readonly>
            <button type="button"
                    class="btn btn-default"
                    (click)="openVideoCaptionFontBrowser(projectMeta, widgetVideoCaptionFontBrowserModal)">
              Browse
            </button>
            <button type="button"
                    class="btn btn-default"
                    (click)="clearVideoCaptionFont(projectMeta)">
              Clear
            </button>
          </div>
        </div>
      </div>
      <div class="col-md-12">
        <div class="mb-3">
          <app-sb-input-radio
            label="Caption Position"
            name="project_caption_position"
            layout="inline"
            [options]="captionPositionOptions"
            [(ngModel)]="projectMeta['widget_profile_video_caption_position']">
          </app-sb-input-radio>
        </div>
        <div class="mb-3">
          <app-sb-input-checkbox
            [id]="'project_widget_profile_video_caption_background_enabled'"
            label="Enable Caption Background"
            [(ngModel)]="projectMeta['widget_profile_video_caption_background_enabled']">
          </app-sb-input-checkbox>
        </div>
      </div>
      @if (projectMeta['widget_profile_video_caption_background_enabled']) {
      <div class="col-md-6">
        <div class="mb-3">
          <app-sb-input-color
            [id]="'project_widget_profile_video_caption_background_color'"
            label="Caption Background Color"
            [(ngModel)]="projectMeta['widget_profile_video_caption_background_color']">
          </app-sb-input-color>
        </div>
        <div class="mb-3">
          <label>Opacity: {{projectMeta['widget_profile_video_caption_background_opacity']}}%</label>
          <input type="range"
                 class="form-range"
                 min="0"
                 max="100"
                 [(ngModel)]="projectMeta['widget_profile_video_caption_background_opacity']">
        </div>
      </div>
      }
      <div class="col-md-6">
        <div class="mb-3">
          <app-sb-input-color
            [id]="'project_widget_profile_video_caption_font_color'"
            label="Font Color"
            [(ngModel)]="projectMeta['widget_profile_video_caption_font_color']">
          </app-sb-input-color>
        </div>
        <div class="mb-3">
          <label>Opacity: {{projectMeta['widget_profile_video_caption_font_opacity']}}%</label>
          <input type="range"
                 class="form-range"
                 min="0"
                 max="100"
                 [(ngModel)]="projectMeta['widget_profile_video_caption_font_opacity']">
        </div>
      </div>
    </div>
    }

    <h6 class="text-uppercase text-muted fw-bold mb-3 mt-3">Background</h6>
    <div class="mb-3">
      <app-sb-input-radio
        label="Background Mode"
        name="project_background_mode"
        layout="inline"
        [options]="videoModeOptions"
        [(ngModel)]="projectMeta['widget_profile_video_background_mode']">
      </app-sb-input-radio>
    </div>
    @if (projectMeta['widget_profile_video_background_mode'] !== 'disabled') {
    <div class="mb-3">
      <app-sb-input-color
        [id]="'project_widget_profile_video_background_color'"
        label="Background Color"
        [(ngModel)]="projectMeta['widget_profile_video_background_color']">
      </app-sb-input-color>
    </div>
    }

    <h6 class="text-uppercase text-muted fw-bold mb-3 mt-3">Teleprompter</h6>
    <div class="mb-3">
      <app-sb-input-checkbox
        [id]="'project_widget_profile_video_enable_teleprompter'"
        label="Enable Teleprompter"
        [(ngModel)]="projectMeta['widget_profile_video_enable_teleprompter']">
      </app-sb-input-checkbox>
    </div>
    @if (projectMeta['widget_profile_video_enable_teleprompter']) {
    <div class="mb-3">
      <label>Speed: {{projectMeta['widget_profile_video_teleprompter_speed']}}ms</label>
      <div class="d-flex align-items-center">
        <span class="me-2">100ms</span>
        <input type="range"
               class="form-range"
               min="100"
               max="500"
               step="10"
               [(ngModel)]="projectMeta['widget_profile_video_teleprompter_speed']">
        <span class="ms-2">500ms</span>
      </div>
    </div>
    }
    @if (projectMeta['widget_profile_video_enable_teleprompter']) {
    <div class="mb-0">
      <app-sb-input-textarea
        [id]="'project_widget_profile_video_teleprompter_question'"
        label="Question"
        description="Can only be used when teleprompter is enabled"
        placeholder="Write your questions here..."
        [rows]="4"
        [(ngModel)]="projectMeta['widget_profile_video_teleprompter_question']">
      </app-sb-input-textarea>
    </div>
    }
    }
  </div>
  <div class="modal-footer">
    <app-sb-button variant="default" label="Cancel" icon="fa fa-times" type="button" (clicked)="modal.dismiss('cancel')"></app-sb-button>
    <app-sb-button variant="primary" label="Save Settings" icon="fa fa-check" type="button" (clicked)="saveWidgetAdvancedSettings(modal)"></app-sb-button>
  </div>
</ng-template>

<ng-template #widgetVideoCaptionFontBrowserModal let-modal>
  <app-iframe-modal-shell
    #fontBrowserShell
    [src]="fontBrowserUrl"
    (close)="modal.dismiss('cancel')">
  </app-iframe-modal-shell>
  <app-file-browser
    #fontFileBrowser
    class="d-none"
    [projectId]="projectId"
    [type]="'font'"
    (fileSelected)="onFontBrowserFileSelected($event)">
  </app-file-browser>
</ng-template>

<ng-template #widgetInstructions let-modal>
  <div class="modal-header">
    <h4 class="modal-title d-flex align-items-center" id="modal-title">
      Widget integration instructions
      <span class="share-link ms-2">
    <a [href]="shareWidgetLinkUrl" target="_blank"><i class="fa fa-share"></i></a>
  </span>
    </h4>
    <button type="button" class="btn-close" aria-label="Close button" aria-describedby="modal-title"
            (click)="modal.dismiss('cancel')"></button>
  </div>
  <div class="modal-body modal-body--iframe">
    @if (widgetIntegrationUrl) {
    <iframe class="widget-integration-frame"
      frameborder="0" width="100%" height="auto"
      [src]="widgetIntegrationUrl">
    </iframe>
    }
  </div>
  <input type="hidden" ngbAutofocus>
  <div class="modal-footer">
    <app-sb-button variant="default" label="Close" type="button" (clicked)="modal.close()"></app-sb-button>
  </div>
</ng-template>

<ng-template #widgetCustomScriptInstructions let-modal>
  <div class="modal-header">
    <h4 class="modal-title" id="modal-title">Custom Script instructions</h4>
    <button type="button" class="btn-close" aria-label="Close button" aria-describedby="modal-title"
            (click)="modal.dismiss('cancel')"></button>
  </div>
  <div class="modal-body">
    @if (widgetCustomScriptBody()) {
    <div [innerHTML]="widgetCustomScriptBody() | sanitizeHtml: 'html'">
    </div>
    }
  </div>
  <input type="hidden" ngbAutofocus>
  <div class="modal-footer">
    <app-sb-button variant="default" label="Close" type="button" (clicked)="modal.close()"></app-sb-button>
  </div>
</ng-template>
